<template>
  <div id="store_detail">
    <div class="detail-header">
      <button class="back-button" @click="goBack">
        <span class="back-icon"></span>
      </button>
      <h2>餐厅详情</h2>
    </div>

    <div v-if="!hasStore" class="empty-detail">
      <p>未找到门店信息</p>
      <button @click="returnToStoreSelection">返回选择餐厅</button>
    </div>

    <div v-else class="detail-body">
      <section class="store-summary">
        <h3>
          {{ store.storename }}
          <span
            v-if="showStoreStatus"
            :class="['status-text', getStoreStatusClass(store)]"
          >（{{ getStoreStatusText(store) }}）</span>
        </h3>
        <p>餐厅代码: {{ store.storecode }}</p>
        <p v-if="store.cityName">城市: {{ store.cityName }}</p>
        <p>地址: {{ store.address }}</p>
        <p>电话: {{ store.phone }}</p>
        <p>营业时间: {{ store.starttime }} - {{ store.endtime }}</p>
        <p v-if="showDistance && store.distance">距离: {{ formatDistance(store.distance) }}</p>
      </section>

      <section v-if="hasStorePhotos" class="section photo-section">
        <h4>门店照片</h4>
        <button
          class="photo-toggle"
          @click="showStorePhotos = !showStorePhotos"
        >{{ showStorePhotos ? '收起门店照片' : '查看门店照片' }}</button>
        <div v-if="showStorePhotos" class="photo-list">
          <img
            v-for="photo in storePhotos"
            :key="photo"
            class="store-photo"
            :src="photo"
            alt="门店照片"
            loading="lazy"
            referrerpolicy="no-referrer"
          >
        </div>
      </section>

      <section class="section">
        <h4>取餐口</h4>
        <div class="option-row">
          <button
            v-for="place in pickupPlaces"
            :key="place.index"
            class="option-button"
            :class="{ selected: selectedPickupIndex === place.index, disabled: place.disabled }"
            :disabled="place.disabled"
            @click="selectPickupPlace(place)"
          >{{ place.name }}</button>
        </div>
      </section>

      <section class="section">
        <h4>取餐方式</h4>
        <div class="option-row">
          <button
            v-for="method in pickupMethods"
            :key="method"
            class="option-button"
            :class="{ selected: pickupMethod === method }"
            @click="pickupMethod = method"
          >{{ method }}</button>
        </div>
      </section>

      <button
        class="copy-button"
        :disabled="disableCopy"
        @click="copyStoreInfo"
      >
        <span class="copy-icon"></span>
        <span>一键复制门店信息</span>
      </button>

      <p v-if="disableCopy" class="disabled-tip">当前餐厅或取餐口未营业，暂不可复制。</p>
      <p v-if="toastText" class="toast-text">{{ toastText }}</p>
    </div>

    <TextToCopy
      v-if="showTextToCopy"
      :storeInfoText="textToCopy"
      @close="showTextToCopy = false"
    />

  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import TextToCopy from './TextToCopy.vue';
import {
  buildStoreInfo,
  copyText,
  findStoredStoreByKey,
  formatDistance,
  getPickupPlaces,
  getStoreStatusClass,
  getStoreStatusText,
  isPickupDisabled,
  saveSelectedStore
} from '../common/js/store';

defineOptions({name: 'RestaurantDetails'});

const route = useRoute();
const router = useRouter();
const store = ref(findStoredStoreByKey(route.params.storeCode));
const selectedPickupIndex = ref(0);
const pickupMethod = ref('堂食');
const pickupMethods = ['堂食', '打包'];
const showTextToCopy = ref(false);
const showStorePhotos = ref(false);
const textToCopy = ref('');
const toastText = ref('');
let disposed = false;

const hasStore = computed(() => !!(store.value && store.value.storecode));
const pickupPlaces = computed(() => getPickupPlaces(store.value));
const showStoreStatus = computed(() => route.query.source !== 'frequent');
const showDistance = computed(() => route.query.source !== 'frequent');
const storePhotos = computed(() => {
  const photos = store.value.storePic && store.value.storePic.storeSelfPic;
  return Array.isArray(photos) ? photos.filter(Boolean) : [];
});
const hasStorePhotos = computed(() => storePhotos.value.length > 0);
const disableCopy = computed(() => isPickupDisabled(store.value, selectedPickupIndex.value));

if (hasStore.value) {
  saveSelectedStore(store.value);
}
if (pickupPlaces.value.length > 0) {
  selectedPickupIndex.value = getFirstEnabledPickupIndex();
}

onBeforeUnmount(() => {
  disposed = true;
});

function goBack () {
  if (!hasStore.value) {
    returnToStoreSelection();
    return;
  }
  if (window.history.length > 1) {
    router.back();
  } else {
    returnToStoreSelection();
  }
}

function returnToStoreSelection () {
  router.replace('/');
}

function getFirstEnabledPickupIndex () {
  const enabledPlaces = pickupPlaces.value.filter(place => !place.disabled);
  const firstPlace = enabledPlaces[0] || pickupPlaces.value[0];
  return firstPlace ? firstPlace.index : 0;
}

function selectPickupPlace (place) {
  if (!place.disabled) {
    selectedPickupIndex.value = place.index;
  }
}

async function copyStoreInfo () {
  const text = buildStoreInfo(store.value, selectedPickupIndex.value, pickupMethod.value);
  textToCopy.value = text;
  toastText.value = '';
  try {
    const success = await copyText(text);
    if (disposed) return;
    if (!success) {
      showTextToCopy.value = true;
      toastText.value = '写入剪贴板失败，请手动复制。';
      return;
    }
    toastText.value = '门店信息已复制到剪贴板。';
  } catch {
    if (!disposed) {
      showTextToCopy.value = true;
      toastText.value = '写入剪贴板失败，请手动复制。';
    }
  }
}
</script>

<style lang="stylus">
#store_detail
  font-family 'Avenir', Helvetica, Arial, sans-serif
  -webkit-font-smoothing antialiased
  -moz-osx-font-smoothing grayscale
  color var(--text-normal)
  height 100%
  width 100%
  font-size 14px
  position absolute
  top 0px
  left 0px
  z-index 1000000000000
  background var(--page-bg)
  box-sizing border-box

  .detail-header
    position relative
    height 48px
    display flex
    align-items center
    justify-content center
    background var(--surface-bg)
    border-bottom 1px solid var(--border-color)

  .detail-header h2
    margin 0
    font-size 20px
    color var(--text-main)

  .back-button
    position absolute
    left 8px
    top 0
    width 44px
    height 48px
    border none
    background transparent
    cursor pointer

  .back-icon
    display inline-block
    width 12px
    height 12px
    border-left 2px solid var(--text-main)
    border-bottom 2px solid var(--text-main)
    transform rotate(45deg)

  .detail-body
    padding 10px
    box-sizing border-box

  .store-summary,
  .section,
  .empty-detail
    background var(--surface-bg)
    border 1px solid var(--border-color)
    border-radius 6px
    padding 12px
    margin-bottom 10px
    box-sizing border-box

  .store-summary h3
    margin 0 0 8px
    color var(--text-main)
    font-size 17px
    line-height 22px

  .store-summary p
    margin 4px 0
    color var(--text-secondary)
    line-height 20px

  .status-text
    font-size 13px
    font-weight normal

  .open
    color var(--success-color)

  .closed
    color var(--accent-color)

  .section h4
    margin 0 0 10px
    font-size 15px
    color var(--text-main)

  .option-row
    display flex
    flex-wrap wrap
    gap 8px

  .option-button
    min-width 86px
    min-height 34px
    padding 6px 10px
    border 1px solid var(--border-strong)
    border-radius 5px
    background var(--surface-bg)
    color var(--text-normal)
    font-size 14px
    cursor pointer
    box-sizing border-box

  .option-button.selected
    border-color var(--accent-color)
    color var(--accent-color)
    font-weight 700

  .option-button:disabled,
  .option-button.disabled
    border-color var(--border-color)
    background var(--disabled-bg)
    color var(--text-faint)
    cursor not-allowed
    font-weight normal

  .photo-section h4
    margin-bottom 8px

  .photo-toggle
    width 100%
    height 38px
    border 1px solid var(--accent-color)
    border-radius 5px
    background var(--surface-bg)
    color var(--accent-color)
    font-size 15px
    cursor pointer

  .photo-list
    margin-top 10px

  .store-photo
    display block
    width 100%
    max-height 260px
    object-fit contain
    border-radius 5px
    background var(--surface-soft)

  .store-photo + .store-photo
    margin-top 8px

  .copy-button,
  .empty-detail button
    width 100%
    height 46px
    border none
    border-radius 5px
    font-size 16px
    cursor pointer

  .copy-button
    display flex
    align-items center
    justify-content center
    gap 9px
    background var(--accent-color)
    color var(--on-accent-color)
    font-size 17px
    font-weight 700

  .copy-icon
    position relative
    display inline-block
    width 16px
    height 18px
    border 2px solid var(--on-accent-color)
    border-radius 3px
    box-sizing border-box

  .copy-icon:before
    content ''
    position absolute
    width 12px
    height 14px
    left -6px
    top -6px
    border 2px solid var(--on-accent-color)
    border-radius 3px
    box-sizing border-box
    opacity 0.72

  .copy-button:disabled
    opacity 0.5
    cursor not-allowed

  .disabled-tip,
  .toast-text
    margin 10px 0 0
    text-align center
    line-height 20px

  .disabled-tip
    color var(--accent-color)

  .toast-text
    color var(--success-color)

  .empty-detail
    margin 10px
    text-align center

  .empty-detail button
    margin-top 10px
    background var(--accent-color)
    color var(--on-accent-color)
</style>
